import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShieldCheck, Loader2, KeyRound, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';

/**
 * OTP verification page, rendered after /auth/login returns `requires_2fa`.
 *
 * Users can either:
 *   - enter their 6-digit code emailed to them (primary flow)
 *   - expand "Use a backup code" and submit one of their 10 single-use codes
 */
export default function TwoFactorVerify() {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();

  const state = location.state || {};
  const [challengeToken, setChallengeToken] = useState(state.challengeToken || '');
  const [emailMasked] = useState(state.emailMasked || '');
  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [usingBackup, setUsingBackup] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!challengeToken) {
      navigate('/login', { replace: true });
      return;
    }
    inputRef.current?.focus();
  }, [challengeToken, navigate]);

  const redirectAfterVerify = (role) => {
    if (role === 'admin') navigate('/admin/z-control', { replace: true });
    else if (role === 'provider') navigate('/provider/dashboard', { replace: true });
    else navigate('/dashboard', { replace: true });
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/auth/2fa/verify', {
        challenge_token: challengeToken, code, remember: rememberDevice,
      });
      try { await checkAuth(); } catch { /* noop */ }
      if (res.data?.device_trusted) {
        toast.success('Welcome back! This device will be remembered for 30 days.');
      } else {
        toast.success('Welcome back!');
      }
      redirectAfterVerify(res.data.role);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackup = async (e) => {
    e.preventDefault();
    const trimmed = (backupCode || '').trim().toUpperCase();
    if (trimmed.length < 8) {
      toast.error('Backup code must be 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/auth/2fa/backup', {
        challenge_token: challengeToken, backup_code: trimmed,
      });
      try { await checkAuth(); } catch { /* noop */ }
      toast.success(
        res.data.backup_codes_remaining === 0
          ? 'Last backup code used. Regenerate a new set from Security settings.'
          : `Signed in. ${res.data.backup_codes_remaining} backup codes left.`,
      );
      redirectAfterVerify(res.data.role);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid backup code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await api.post('/auth/2fa/resend', { challenge_token: challengeToken });
      if (res.data?.challenge_token) setChallengeToken(res.data.challenge_token);
      toast.success('A new code has been sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not resend, please sign in again');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full">
        <div className="card-surface p-8" data-testid="two-factor-verify">
          <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-4">
            <ShieldCheck className="h-6 w-6 text-[#D4AF37]" />
          </div>
          <h1 className="font-heading text-2xl font-medium text-[#1A1A1A]">Two-factor verification</h1>
          <p className="text-stone-500 text-sm mt-2 leading-relaxed">
            {usingBackup ? (
              <>Enter one of your single-use backup codes to finish signing in.</>
            ) : (
              <>We've sent a 6-digit code to <strong>{emailMasked || 'your email'}</strong>. Check your inbox (and spam), it expires in 10 minutes.</>
            )}
          </p>

          {!usingBackup ? (
            <form onSubmit={handleVerify} className="mt-6 space-y-4" data-testid="otp-form">
              <Input
                ref={inputRef}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="text-center text-2xl tracking-[0.5em] font-mono border-[#E6E4DD] h-14"
                data-testid="otp-input"
              />
              <label className="flex items-start gap-2 text-xs text-stone-600 cursor-pointer select-none" data-testid="remember-device-label">
                <Checkbox
                  checked={rememberDevice}
                  onCheckedChange={(v) => setRememberDevice(Boolean(v))}
                  className="mt-0.5"
                  data-testid="remember-device-checkbox"
                />
                <span>
                  Remember this device for 30 days. We'll skip the code next time you sign in from this browser.
                </span>
              </label>
              <Button
                type="submit"
                disabled={submitting || code.length !== 6}
                className="w-full bg-[#1A1A1A] hover:bg-black text-white gap-2"
                data-testid="otp-submit"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                {submitting ? 'Verifying...' : 'Verify & sign in'}
              </Button>
              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="text-stone-500 hover:text-[#1A1A1A] underline-offset-2 hover:underline disabled:opacity-60"
                  data-testid="otp-resend"
                >
                  {resending ? 'Sending...' : 'Resend code'}
                </button>
                <button
                  type="button"
                  onClick={() => setUsingBackup(true)}
                  className="text-stone-500 hover:text-[#1A1A1A] inline-flex items-center gap-1 underline-offset-2 hover:underline"
                  data-testid="use-backup-code"
                >
                  <KeyRound className="h-3 w-3" />
                  Use a backup code
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleBackup} className="mt-6 space-y-4" data-testid="backup-code-form">
              <Input
                placeholder="ABCD-1234"
                value={backupCode}
                onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                maxLength={9}
                className="text-center text-lg tracking-widest font-mono border-[#E6E4DD] h-14 uppercase"
                data-testid="backup-code-input"
              />
              <Button
                type="submit"
                disabled={submitting || backupCode.replace('-', '').length < 8}
                className="w-full bg-[#1A1A1A] hover:bg-black text-white gap-2"
                data-testid="backup-code-submit"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                Use backup code
              </Button>
              <button
                type="button"
                onClick={() => setUsingBackup(false)}
                className="text-xs text-stone-500 hover:text-[#1A1A1A] inline-flex items-center gap-1"
                data-testid="back-to-otp"
              >
                <ArrowLeft className="h-3 w-3" /> Back to email code
              </button>
            </form>
          )}
        </div>
        <p className="text-center text-xs text-stone-400 mt-4">
          Lost your email access and backup codes? <a href="/contact" className="underline underline-offset-2">Contact support</a>.
        </p>
      </div>
    </div>
  );
}
